import React, { Fragment, useEffect } from 'react'
import { SnackBar } from '../../components//SnackBar/Snackbar'
import { Modal } from '../../components//_EOS/Modal'
import { useModal } from '../../hooks/useModal'
import { useSnackbar } from '../../hooks/useSnackbar'
import { Zone } from '../../interfaces'
import { useApi } from '../../hooks/useApi'
import { getZones, postZone, putZone } from './config/api'
import { Status } from '../../hooks/useFetchState'
import { StandardPage } from '../../components/_EOS/StandardPage'
import {
  GenericOrderBy,
  GenericTable,
  getNewDirection,
  TableHeaderConfigWithType
} from '../../components/_EOS/Table'
import { ApiResponse } from '../../typings/ApiResponse'
import { usePagination } from '../../hooks/usePagination'
import {
  Filter,
  FilterComponent,
  FilterOption
} from '../../components/_WMS/FilterComponent'
import { State } from '../../components/_EOS/State'
import ZoneCreateModal from './Modals'
import { Loading } from '../../components/UI'

interface ZonePageProps {}

export const ZonePage: React.FC<ZonePageProps> = () => {
  const filterOptions: FilterOption[] = [
    { key: 'warehouseId', label: 'Almacén', order: 1, type: 'precreated' },
    { key: 'zoneName', label: 'Zona', order: 2, type: 'text' }
  ]
  const [searchFilters, setSearchFiltes] = React.useState<Filter[]>([])

  const createZoneModalState = useModal('Crear')
  const editZoneModalState = useModal<Zone>('Editar Zona')

  const snackbarState = useSnackbar()

  const { state: createZoneState, callApi: createZoneCallApi } = useApi()
  const { state: editZoneState, callApi: editZoneCallApi } = useApi()
  const { state: getZonesState, callApi: getZonesCallApi } =
    useApi<ApiResponse<Zone>>()

  const requestGetZones = (
    page: number,
    pageSize: number,
    _filters?: Filter[]
  ) => {
    getZonesCallApi(getZones(page, pageSize, searchFilters, orderBy))
  }

  const requestCreateZone = (zone: Zone) => {
    createZoneCallApi(postZone(zone))
      .then(() => {
        requestGetZones(0, pagination.pageSize)
        createZoneModalState.closeModal()
        snackbarState.openSnackBar('Zona creada correctamente', true)
      })
      .catch((err) => snackbarState.openSnackBar(err, false))
  }

  const requestUpdateZone = (zone: Zone) => {
    editZoneCallApi(putZone(zone.zoneId, zone))
      .then(() => {
        requestGetZones(0, pagination.pageSize)
        snackbarState.openSnackBar('Zona actualizada correctamente', true)
        editZoneModalState.closeModal()
      })
      .catch((err) => snackbarState.openSnackBar(err, false))
  }

  const headers: TableHeaderConfigWithType<Zone>[] = [
    {
      field: 'zoneId',
      title: 'ID',
      sortable: true
    },
    {
      field: 'zoneName',
      title: 'Nombre Zona',
      sortable: true
    },
    {
      field: 'warehouseId',
      title: 'Almacén',
      formater: (_value, row) => row?.warehouse.warehouseName || 'Sin Almacén',
      sortable: true
    },
    {
      field: 'active',
      title: 'Estado',
      formater: (value) => (value === true ? 'Activo' : 'Inactivo')
    },
    {
      field: 'externalStorage',
      title: 'Externa',
      formater: (value) => (value === true ? 'Si' : 'No')
    }
  ]

  const pagination = usePagination(5)

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestGetZones(auxPage, pageSize, searchFilters)
    pagination.setPagination(auxPage, pageSize)
  }

  const [orderBy, setOrderBy] = React.useState<GenericOrderBy<Zone>>({
    field: '',
    direction: 'desc'
  })

  const handleSorting = ({ field, direction }: GenericOrderBy<Zone>) => {
    if (field === orderBy.field) {
      const newDirection = getNewDirection(orderBy.direction)
      setOrderBy((_prev) => ({ field, direction: newDirection }))
      return
    }

    setOrderBy({ field, direction })
  }

  useEffect(() => {
    requestGetZones(pagination.page, pagination.pageSize, searchFilters)
  }, [orderBy, searchFilters])

  return (
    <Fragment>
      <StandardPage
        pageTitle='Administrador de Zonas'
        buttonAction={() => createZoneModalState.openModal()}
        buttonTitle='Crear Zona'
      >
        <FilterComponent
          options={filterOptions}
          onSearchClick={(f) =>
            requestGetZones(pagination.page, pagination.pageSize, f)
          }
          selectedFilters={searchFilters}
          setSelectedFilters={setSearchFiltes}
        />
        <State state={getZonesState} loadingMessage='Obteniendo zonas'>
          <GenericTable<Zone>
            headersConfig={headers}
            IDKey={'active'}
            data={getZonesState.data?.items}
            actions={{
              onEdit: (_id, row) =>
                editZoneModalState.openModal(editZoneModalState.size, row)
            }}
            pagination={{
              page: pagination.page || 0,
              pageSize: pagination.pageSize || 5,
              totalItem: getZonesState.data?.totalItems || 0
            }}
            onPaginationChange={handlePaginationChange}
            onSortChange={handleSorting}
            orderField={orderBy}
          />
        </State>
      </StandardPage>

      <Modal
        open={editZoneModalState.isOpen}
        onClose={editZoneModalState.closeModal}
        title={editZoneModalState.title}
        loading={editZoneState.status === Status.Loading}
      >
        {editZoneState.status === Status.Loading && <Loading />}
        {editZoneState.status !== Status.Loading &&
          editZoneModalState.extendedProps && (
            <ZoneCreateModal
              editingElement={editZoneModalState.extendedProps}
              handleSubitting={requestUpdateZone}
            />
          )}
      </Modal>

      <Modal
        open={createZoneModalState.isOpen}
        onClose={createZoneModalState.closeModal}
        title={createZoneModalState.title}
        loading={createZoneState.status === Status.Loading}
      >
        {createZoneState.status === Status.Loading && <Loading />}
        {createZoneState.status !== Status.Loading && (
          <ZoneCreateModal handleSubitting={requestCreateZone} />
        )}
      </Modal>

      <SnackBar
        open={snackbarState.isActive}
        message={snackbarState.message}
        status={snackbarState.isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
